import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Button } from "../components/ui/button";
import { Download } from "lucide-react";

const formatCurrency = (val) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val);
const TOOLTIP_STYLE = { borderRadius: '4px', border: '1px solid #E5E5E3', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' };
const LEGEND_STYLE = { fontSize: '12px' };

export default function Report() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAssistant = user?.role === "assistant";
  const isStaff = isAdmin || isAssistant;
  const [transactions, setTransactions] = useState([]);
  const [tags, setTags] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchData = useCallback(async () => {
    try {
      const [txRes, tagsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/transactions`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tags`, { withCredentials: true })
      ]);
      setTransactions(txRes.data);
      setTags(tagsRes.data);
    } catch (e) {
      toast.error("Rapor verileri alınamadı.");
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculations for TL
  const tlTxs = useMemo(() => transactions.filter(t => t.currency === "TL"), [transactions]);

  const totalIncome = useMemo(() => {
    return tlTxs.filter(t => t.type === "income").reduce((acc, t) => acc + t.amount, 0);
  }, [tlTxs]);

  const totalDebtPayment = useMemo(() => {
    return tlTxs.filter(t => t.type === "debt_payment").reduce((acc, t) => acc + t.amount, 0);
  }, [tlTxs]);

  const totalExpense = useMemo(() => {
    return tlTxs.filter(t => t.type === "expense").reduce((acc, t) => acc + t.amount, 0);
  }, [tlTxs]);

  const netIncome = totalIncome - totalDebtPayment;

  const handleExportReport = () => {
    window.open(`${BACKEND_URL}/api/report/export`, '_blank');
  };

  const accountExpenses = useMemo(() => {
    const accMap = {};
    const expenses = tlTxs.filter(t => t.type === "expense");
    const colors = ['#276339', '#A13426', '#2563eb', '#eab308', '#8b5cf6', '#d946ef', '#14b8a6', '#06b6d4', '#f97316', '#64748b'];

    expenses.forEach(tx => {
      const accName = tx.account_name && tx.account_name.trim() ? tx.account_name.trim() : "Diğer / İsimsiz";
      
      if (!accMap[accName]) {
        const colorIndex = Object.keys(accMap).length % colors.length;
        accMap[accName] = { name: accName, value: 0, color: colors[colorIndex] };
      }
      accMap[accName].value += tx.amount;
    });

    return Object.values(accMap).sort((a, b) => b.value - a.value);
  }, [tlTxs]);

  if (!isStaff) {
    return <div className="p-8 text-center text-muted-foreground">Bu sayfayı görüntüleme yetkiniz yok.</div>;
  }

  return (
    <div className="space-y-8 pb-10 max-w-5xl">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <h1 className="text-3xl font-heading font-bold">Detaylı Finansal Rapor (TL)</h1>
        <Button onClick={handleExportReport} className="h-10 rounded-sm" data-testid="export-report-btn">
          <Download size={16} className="mr-2" /> Raporu Excel İndir
        </Button>
      </div>

      <div className="max-w-2xl">
        <Card className="swiss-card">
          <CardHeader className="border-b border-border pb-4 bg-neutral-50/50">
            <CardTitle className="text-sm font-bold uppercase tracking-[0.2em] text-muted-foreground">Genel Durum Özeti</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-5">
            <div className="flex justify-between items-center pb-5 border-b border-dashed border-border">
              <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Toplam Gelir</span>
              <span className="text-xl font-bold font-heading text-income">{formatCurrency(totalIncome)}</span>
            </div>
            <div className="flex justify-between items-center pb-5 border-b border-dashed border-border">
              <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Borç Ödemeleri</span>
              <span className="text-xl font-bold font-heading text-blue-600">- {formatCurrency(totalDebtPayment)}</span>
            </div>
            <div className="flex justify-between items-center pb-5 border-b border-dashed border-border">
              <div className="flex flex-col">
                <span className="text-sm font-bold uppercase tracking-wider text-primary">Kalan Net Gelir</span>
                <span className="text-[10px] text-muted-foreground">(Toplam Gelir - Borç Ödemeleri)</span>
              </div>
              <span className="text-xl font-bold font-heading text-primary">{formatCurrency(netIncome)}</span>
            </div>
            <div className="flex justify-between items-center pb-5 border-b border-dashed border-border">
              <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Toplam Gider</span>
              <span className="text-xl font-bold font-heading text-expense">- {formatCurrency(totalExpense)}</span>
            </div>
            <div className="flex justify-between items-center pt-2">
              <div className="flex flex-col">
                <span className="text-sm font-bold uppercase tracking-wider text-primary">Net Kasa Toplamı</span>
                <span className="text-[10px] text-muted-foreground">(Kalan Net Gelir - Toplam Gider)</span>
              </div>
              <span className="text-3xl font-bold font-heading text-primary">{formatCurrency(netIncome - totalExpense)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-10 space-y-6">
        <h2 className="text-2xl font-heading font-bold tracking-tight border-b border-border pb-2">Cari Hesap Bazlı Gider Dağılımı (Sadece Çıkışlar)</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Table View */}
          <div className="bg-white border border-border rounded-sm overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
            <table className="w-full text-sm text-left">
              <thead className="bg-background text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
                <tr>
                  <th className="px-5 py-4">Cari Hesap / Kişi</th>
                  <th className="px-5 py-4 text-right">Toplam Gider</th>
                </tr>
              </thead>
              <tbody>
                {accountExpenses.length === 0 ? (
                  <tr><td colSpan="2" className="text-center py-10 text-muted-foreground">Gider kaydı bulunamadı.</td></tr>
                ) : (
                  <>
                    {accountExpenses.map((acc, idx) => (
                      <tr key={idx} className="border-b border-border hover:bg-neutral-50/50 transition-colors">
                        <td className="px-5 py-4 font-medium flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: acc.color }}></div>
                          {acc.name}
                        </td>
                        <td className="px-5 py-4 text-right font-medium font-heading tracking-tight text-base text-expense">
                          {formatCurrency(acc.value)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-neutral-50/80 border-t-2 border-border">
                      <td className="px-5 py-4 font-bold uppercase tracking-widest text-muted-foreground text-xs">
                        Genel Toplam Gider
                      </td>
                      <td className="px-5 py-4 text-right font-bold font-heading tracking-tight text-lg text-expense">
                        {formatCurrency(totalExpense)}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Chart View */}
          <Card className="swiss-card h-full min-h-[300px]">
            <CardHeader>
              <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">Giderlerin Görsel Dağılımı</CardTitle>
            </CardHeader>
            <CardContent className="h-[300px] w-full">
              {accountExpenses.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={accountExpenses}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {accountExpenses.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => `₺${value}`} />
                    <Legend iconType="circle" layout="vertical" verticalAlign="middle" align="right" wrapperStyle={LEGEND_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Veri yok.</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
